import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Alert,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { productService } from '../../services/products';
import { Product } from '../../types';
import { useTranslation } from 'react-i18next';

// Extracted Components
import ProductManagementHeader from './product-management/components/ProductManagementHeader';
import ProductFilters from './product-management/components/ProductFilters';
import ProductTable from './product-management/components/ProductTable';
import ProductActionMenu from './product-management/components/ProductActionMenu';
import DeleteProductDialog from './product-management/components/DeleteProductDialog';

const ProductManagement: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const basePath = user?.role === 'seller' ? '/seller/products' : '/admin/products';
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    fetchProducts();
  }, [user]);

  const fetchProducts = async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error } = await productService.getProductsBySeller(user?.id || '');
      if (error) throw error;
      setProducts(data || []);
    } catch (err: any) {
      setError(t('common.error'));
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, product: Product) => {
    setAnchorEl(event.currentTarget);
    setSelectedProduct(product);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (!selectedProduct) return;
    
    try {
      const { error } = await productService.deleteProduct(selectedProduct.id);
      if (error) throw error;
      
      setSuccess(t('admin.productDeleted'));
      setProducts(products.filter(p => p.id !== selectedProduct.id));
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(t('common.error'));
    }
    
    setDeleteDialogOpen(false);
    setSelectedProduct(null);
  };

  const handleStatusToggle = async (product: Product) => {
    const newStatus = product.status === 'available' ? 'sold' : 'available';
    try {
      const { error } = await productService.updateProduct(product.id, { status: newStatus });
      if (error) throw error;
      
      setProducts(products.map(p => 
        p.id === product.id ? { ...p, status: newStatus } : p
      ));
      setSuccess(t('admin.productMarkedAs', { status: newStatus }));
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(t('admin.failedToUpdateStatus'));
    }
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !filterCategory || product.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const paginatedProducts = filteredProducts.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 6 }}>
      <ProductManagementHeader basePath={basePath} />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <ProductFilters
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        filterCategory={filterCategory}
        onCategoryChange={setFilterCategory}
      />

      <ProductTable
        loading={loading}
        products={paginatedProducts}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={filteredProducts.length}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        onStatusToggle={handleStatusToggle}
        onMenuClick={handleMenuClick}
        basePath={basePath}
      />

      <ProductActionMenu
        anchorEl={anchorEl}
        onClose={handleMenuClose}
        selectedProduct={selectedProduct}
        basePath={basePath}
        onDeleteClick={handleDeleteClick}
      />

      <DeleteProductDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        product={selectedProduct}
      />
    </Container>
  );
};

export default ProductManagement;